import { motion } from "framer-motion";
import { ArrowRight, Trophy } from "lucide-react";
import { ASSETS } from "@/data";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const stats = [
  { value: "25", label: "Χρόνια εμπειρίας" },
  { value: "5", label: "Μαθητές / τμήμα" },
];

export default function Hero() {
  return (
    <section id="hero" data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-1.5 mb-8"
            >
              <span className="h-2 w-2 rounded-full bg-accent animate-pulse" />
              <span className="font-mono-accent text-xs uppercase tracking-widest text-muted-foreground">
                Εγγραφές 2026 — 2027 σε εξέλιξη
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]"
            >
              Η τέχνη της
              <span className="block text-primary">επιτυχίας.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              Στη <strong className="text-foreground">Φυσική Επιλογή</strong>, θα έχεις την ευκαιρία,
              σαν μαθητής Γυμνασίου, να εκπαιδευτείς στη μεθοδικότητα και τη συνέπεια. Έτσι, σαν
              μαθητής Λυκείου, οργανωμένος πια, θα ανταγωνιστείς τους καλύτερους και θα επιτύχεις!
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-cta-enroll"
                onClick={() => scrollTo("enroll")}
                className="group inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-accent text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-accent transition-colors"
              >
                Εγγραφές 2026
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-cta-successes"
                onClick={() => scrollTo("news")}
                className="inline-flex items-center justify-center gap-2 border border-foreground/20 font-mono-accent text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:border-foreground transition-colors"
              >
                <Trophy className="h-4 w-4" />
                Οι Επιτυχίες μας
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-14 grid grid-cols-2 max-w-md border-t border-border"
            >
              {stats.map((s) => (
                <div key={s.label} className="pt-5 pr-4">
                  <div className="font-heading text-3xl font-extrabold text-primary">{s.value}</div>
                  <div className="font-mono-accent text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 h-full w-full border border-primary/30 rounded-sm" />
              <img
                data-testid="hero-image"
                src={ASSETS.interior}
                alt="Ο χώρος του φροντιστηρίου Φυσική Επιλογή"
                className="relative w-full h-[380px] lg:h-[520px] object-cover rounded-sm grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur border border-border p-4 rounded-sm">
                <div className="font-mono-accent text-[10px] uppercase tracking-widest text-accent">// Natural Selection</div>
                <div className="font-heading font-bold mt-1">Αγωνιζόμαστε για την αριστεία. Κάθε μέρα.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
